import axios from 'axios';
import { notificationsAPI } from './notifications';
import { returnsAPI } from './returnsAPI';
import { salesAPI } from './salesAPI';
 import { ItemReceipt  } from '../components/branch/types';

const API_BASE_URL = 'https://aljodia.com/api';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

export const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`[${new Date().toISOString()}] API request:`, {
      url: config.url,
      method: config.method,
      headers: config.headers,
    });
    return config;
  },
  (error) => {
    console.error(`[${new Date().toISOString()}] API request error:`, error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;
    console.error(`[${new Date().toISOString()}] API response error:`, {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });

    let message = error.response?.data?.message || 'خطأ غير متوقع';
    if (error.response?.status === 400) message = error.response?.data?.message || 'بيانات غير صالحة';
    if (error.response?.status === 403) message = error.response?.data?.message || 'عملية غير مصرح بها';
    if (error.response?.status === 404) message = error.response?.data?.message || 'المورد غير موجود';
    if (error.response?.status === 429) message = 'طلبات كثيرة جدًا، حاول مرة أخرى لاحقًا';

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.error(`[${new Date().toISOString()}] No refresh token available`);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
         
          return Promise.reject({ message: 'التوكن منتهي الصلاحية ولا يوجد توكن منعش', status: 401 });
        }
        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken });
        const { token, refreshToken: newRefreshToken } = response.data;
        localStorage.setItem('token', token);
        if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
        console.log(`[${new Date().toISOString()}] Token refreshed successfully`);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error(`[${new Date().toISOString()}] Refresh token failed:`, refreshError);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
       
        return Promise.reject({ message: 'فشل تجديد التوكن', status: 401 });
      }
    }

    return Promise.reject({ message, status: error.response?.status });
  }
);

export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', {
      username: credentials.username.trim(),
      password: credentials.password,
    });
    console.log(`[${new Date().toISOString()}] Login response:`, response);
    return response;
  },
  refreshToken: async (refreshToken) => {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken });
    console.log(`[${new Date().toISOString()}] Refresh token response:`, response.data);
    return response.data;
  },
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    console.log(`[${new Date().toISOString()}] Profile response:`, response);
    return {
      ...response,
      user: {
        ...response.user,
        id: response.user.id || response.user._id,
      },
    };
  },
  updateProfile: async (data) => {
    const response = await api.put('/auth/update-profile', {
      name: data.name?.trim(),
      nameEn: data.nameEn?.trim(),
      email: data.email?.trim(),
      phone: data.phone?.trim(),
      password: data.password,
    });
    console.log(`[${new Date().toISOString()}] Update profile response:`, response);
    return response;
  },
  checkEmail: async (email) => {
    const response = await api.post('/auth/check-email', { email: email.trim() });
    console.log(`[${new Date().toISOString()}] Check email response:`, response);
    return response;
  },
};


export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/users');
    console.log(`[${new Date().toISOString()}] usersAPI.getAll - Response:`, response);
    return response;
  },
  getById: async (id) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] usersAPI.getById - Invalid user ID:`, id);
      throw new Error('Invalid user ID');
    }
    const response = await api.get(`/users/${id}`);
    console.log(`[${new Date().toISOString()}] usersAPI.getById - Response:`, response);
    return response;
  },
  create: async (userData) => {
    if (userData.role === 'branch' && userData.branch && !isValidObjectId(userData.branch)) {
      console.error(`[${new Date().toISOString()}] usersAPI.create - Invalid branch ID:`, userData.branch);
      throw new Error('Invalid branch ID');
    }
    const response = await api.post('/users', {
      name: userData.name.trim(),
      nameEn: userData.nameEn?.trim(),
      username: userData.username.trim(),
      password: userData.password,
      email: userData.email?.trim(),
      phone: userData.phone?.trim(),
      role: userData.role,
      branch: userData.branch,
      isActive: userData.isActive ?? true,
    });
    console.log(`[${new Date().toISOString()}] usersAPI.create - Response:`, response);
    return response;
  },
  update: async (id, userData) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] usersAPI.update - Invalid user ID:`, id);
      throw new Error('Invalid user ID');
    }
    if (userData.role === 'branch' && userData.branch && !isValidObjectId(userData.branch)) {
      console.error(`[${new Date().toISOString()}] usersAPI.update - Invalid branch ID:`, userData.branch);
      throw new Error('Invalid branch ID');
    }
    const response = await api.put(`/users/${id}`, {
      name: userData.name?.trim(),
      nameEn: userData.nameEn?.trim(),
      username: userData.username?.trim(),
      email: userData.email?.trim(),
      phone: userData.phone?.trim(),
      role: userData.role,
      branch: userData.branch,
      isActive: userData.isActive ?? true,
    });
    console.log(`[${new Date().toISOString()}] usersAPI.update - Response:`, response);
    return response;
  },
  delete: async (id) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] usersAPI.delete - Invalid user ID:`, id);
      throw new Error('Invalid user ID');
    }
    const response = await api.delete(`/users/${id}`);
    console.log(`[${new Date().toISOString()}] usersAPI.delete - Response:`, response);
    return response;
  },
  checkEmail: async (email) => {
    const response = await api.post('/users/check-email', { email: email.trim() });
    console.log(`[${new Date().toISOString()}] usersAPI.checkEmail - Response:`, response);
    return response;
  },
  resetPassword: async (id, password) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] usersAPI.resetPassword - Invalid user ID:`, id);
      throw new Error('Invalid user ID');
    }
    const response = await api.post(`/users/${id}/reset-password`, { password });
    console.log(`[${new Date().toISOString()}] usersAPI.resetPassword - Response:`, response);
    return response;
  },
};

export const productsAPI = {
  getAll: async (params: {
    limit?: number;
    page?: number;
    search?: string;
    department?: string;
    isActive?: boolean;
  } = {}) => {
    if (params.department && !isValidObjectId(params.department)) {
      throw new Error('Invalid department ID');
    }
    
    const response = await api.get('/products', { params });
    return response;
  },

  getById: async (id: string) => {
    if (!isValidObjectId(id)) {
      throw new Error('Invalid product ID');
    }
    
    const response = await api.get(`/products/${id}`);
    return response;
  },

  create: async (productData: {
    name: string;
    nameEn?: string;
    code: string;
    department: string;
    price: number;
    unit?: string;
    description?: string;
    image?: string;
    isActive?: boolean;
  }) => {
    if (!isValidObjectId(productData.department)) {
      throw new Error('Invalid department ID');
    }

    const payload = {
      name: productData.name.trim(),
      nameEn: productData.nameEn?.trim(),
      code: productData.code.trim(),
      department: productData.department,
      price: productData.price,
      unit: productData.unit?.trim(),
      description: productData.description?.trim(),
      image: productData.image,
      isActive: Boolean(productData.isActive),
    };

    const response = await api.post('/products', payload);
    return response;
  },

  update: async (id: string, productData: {
    name?: string;
    nameEn?: string;
    code?: string;
    department?: string;
    price?: number;
    unit?: string;
    description?: string;
    image?: string;
    isActive?: boolean;
  }) => {
    if (!isValidObjectId(id)) {
      throw new Error('Invalid product ID');
    }
    
    if (productData.department && !isValidObjectId(productData.department)) {
      throw new Error('Invalid department ID');
    }

    const payload = {
      name: productData.name?.trim(),
      nameEn: productData.nameEn?.trim(),
      code: productData.code?.trim(),
      department: productData.department,
      price: productData.price,
      unit: productData.unit?.trim(),
      description: productData.description?.trim(),
      image: productData.image,
      isActive: productData.isActive !== undefined ? Boolean(productData.isActive) : undefined,
    };

    const response = await api.put(`/products/${id}`, payload);
    return response;
  },

  delete: async (id: string) => {
    if (!isValidObjectId(id)) {
      throw new Error('Invalid product ID');
    }
    
    const response = await api.delete(`/products/${id}`);
    return response;
  }
};






const createErrorMessage = (errorType, isRtl) => {
  const messages = {
    invalidBranchId: { ar: 'معرف الفرع غير صالح', en: 'Invalid branch ID' },
    invalidOrderId: { ar: 'معرف الطلب غير صالح', en: 'Invalid order ID' },
    invalidDepartmentId: { ar: 'معرف القسم غير صالح', en: 'Invalid department ID' },
    invalidProductId: { ar: 'معرف المنتج غير صالح', en: 'Invalid product ID' },
    invalidOrderOrTaskId: { ar: 'معرف الطلب أو المهمة غير صالح', en: 'Invalid order or task ID' },
    invalidOrderOrItemOrChefId: { ar: 'معرف الطلب، العنصر، أو الشيف غير صالح', en: 'Invalid order, item, or chef ID' },
    invalidStockQuantity: { ar: 'كمية المخزون يجب أن تكون غير سالبة', en: 'Stock quantity must be non-negative' },
    invalidMinStockLevel: { ar: 'الحد الأدنى للمخزون يجب أن يكون عددًا صحيحًا غير سالب', en: 'Minimum stock level must be a non-negative integer' },
    invalidMaxStockLevel: { ar: 'الحد الأقصى للمخزون يجب أن يكون عددًا صحيحًا غير سالب', en: 'Maximum stock level must be a non-negative integer' },
    maxLessThanMin: { ar: 'الحد الأقصى يجب أن يكون أكبر من الحد الأدنى', en: 'Maximum stock level must be greater than minimum' },
 
    invalidUserId: { ar: 'معرف المستخدم غير صالح', en: 'Invalid user ID' },
    invalidInventoryId: { ar: 'معرف المخزون غير صالح', en: 'Invalid inventory ID' },
    invalidStatus: { ar: 'الحالة غير صالحة', en: 'Invalid status' },
    invalidFactoryOrderId: { ar: 'معرف طلب المصنع غير صالح', en: 'Invalid factory order ID' },
    invalidId: { ar: 'معرف غير صالح', en: 'Invalid ID' },
    invalidItems: { ar: 'العناصر غير صالحة', en: 'Invalid items' },
    invalidData: { ar: 'البيانات غير صالحة', en: 'Invalid data' },
  };
  return messages[errorType] ? (isRtl ? messages[errorType].ar : messages[errorType].en) : (isRtl ? 'خطأ غير معروف' : 'Unknown error');
};

export const branchesAPI = {
  getAll: async () => {
    const response = await api.get('/branches');
    console.log(`[${new Date().toISOString()}] Branches getAll response:`, response);
    return response;
  },
  getById: async (id) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] Branches getById - Invalid branch ID:`, id);
      throw new Error('Invalid branch ID');
    }
    const response = await api.get(`/branches/${id}`);
    console.log(`[${new Date().toISOString()}] Branches getById response:`, response);
    return response;
  },
  create: async (branchData) => {
    const response = await api.post('/branches', {
      name: branchData.name.trim(),
      nameEn: branchData.nameEn?.trim(),
      code: branchData.code.trim(),
      address: branchData.address.trim(),
      addressEn: branchData.addressEn?.trim(),
      city: branchData.city.trim(),
      cityEn: branchData.cityEn?.trim(),
      phone: branchData.phone?.trim(),
      user: {
        name: branchData.user.name.trim(),
        nameEn: branchData.user.nameEn?.trim(),
        username: branchData.user.username.trim(),
        password: branchData.user.password,
        email: branchData.user.email?.trim(),
        phone: branchData.user.phone?.trim(),
        isActive: branchData.user.isActive ?? true,
      },
    });
    console.log(`[${new Date().toISOString()}] Branches create response:`, response);
    return response;
  },
  update: async (id, branchData) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] Branches update - Invalid branch ID:`, id);
      throw new Error('Invalid branch ID');
    }
    const response = await api.put(`/branches/${id}`, {
      name: branchData.name.trim(),
      nameEn: branchData.nameEn?.trim(),
      code: branchData.code.trim(),
      address: branchData.address.trim(),
      addressEn: branchData.addressEn?.trim(),
      city: branchData.city.trim(),
      cityEn: branchData.cityEn?.trim(),
      phone: branchData.phone?.trim(),
      user: {
        name: branchData.user.name.trim(),
        nameEn: branchData.user.nameEn?.trim(),
        username: branchData.user.username.trim(),
        email: branchData.user.email?.trim(),
        phone: branchData.user.phone?.trim(),
        isActive: branchData.user.isActive ?? true,
      },
    });
    console.log(`[${new Date().toISOString()}] Branches update response:`, response);
    return response;
  },
  delete: async (id) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] Branches delete - Invalid branch ID:`, id);
      throw new Error('Invalid branch ID');
    }
    const response = await api.delete(`/branches/${id}`);
    console.log(`[${new Date().toISOString()}] Branches delete response:`, response);
    return response;
  },
  checkEmail: async (email) => {
    const response = await api.post('/branches/check-email', { email: email.trim() });
    console.log(`[${new Date().toISOString()}] Branches checkEmail response:`, response);
    return response;
  },
  resetPassword: async (id, password) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] Branches resetPassword - Invalid branch ID:`, id);
      throw new Error('Invalid branch ID');
    }
    const response = await api.post(`/branches/${id}/reset-password`, { password });
    console.log(`[${new Date().toISOString()}] Branches resetPassword response:`, response);
    return response;
  },
};








export const chefsAPI = {
  getAll: async (params?: { isRtl?: boolean }) => {
    const response = await api.get('/chefs', { params });
    console.log(`[${new Date().toISOString()}] chefsAPI.getAll - Response:`, response);
    return response;
  },
  getById: async (id: string) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] chefsAPI.getById - Invalid chef ID:`, id);
      throw new Error('Invalid chef ID');
    }
    const response = await api.get(`/chefs/${id}`);
    console.log(`[${new Date().toISOString()}] chefsAPI.getById - Response:`, response);
    return response;
  },
  getByUserId: async (userId: string) => {
    if (!isValidObjectId(userId)) {
      console.error(`[${new Date().toISOString()}] chefsAPI.getByUserId - Invalid user ID:`, userId);
      throw new Error('Invalid user ID');
    }
    const response = await api.get(`/chefs/by-user/${userId}`);
    console.log(`[${new Date().toISOString()}] chefsAPI.getByUserId - Response:`, response);
    return response;
  },
  create: async (chefData: {
    user: {
      name: string;
      nameEn?: string;
      username: string;
      email?: string;
      phone?: string;
      password: string;
      role?: string;
      isActive?: boolean;
    };
    department: string[];
  }) => {
    if (!chefData.department.every(id => isValidObjectId(id))) {
      console.error(`[${new Date().toISOString()}] chefsAPI.create - Invalid department ID:`, chefData.department);
      throw new Error('Invalid department ID');
    }
    const response = await api.post('/chefs', {
      user: {
        name: chefData.user.name.trim(),
        nameEn: chefData.user.nameEn?.trim(),
        username: chefData.user.username.trim(),
        email: chefData.user.email?.trim(),
        phone: chefData.user.phone?.trim(),
        password: chefData.user.password,
        role: chefData.user.role || 'chef',
        isActive: chefData.user.isActive ?? true,
      },
      department: chefData.department,
    });
    console.log(`[${new Date().toISOString()}] chefsAPI.create - Response:`, response);
    return response;
  },
  update: async (id: string, chefData: {
    user: {
      name: string;
      nameEn?: string;
      username: string;
      email?: string;
      phone?: string;
      isActive?: boolean;
    };
    department: string[];
  }) => {
    if (!isValidObjectId(id) || !chefData.department.every(id => isValidObjectId(id))) {
      console.error(`[${new Date().toISOString()}] chefsAPI.update - Invalid chef ID or department ID:`, { id, department: chefData.department });
      throw new Error('Invalid chef ID or department ID');
    }
    const response = await api.put(`/chefs/${id}`, {
      user: {
        name: chefData.user.name.trim(),
        nameEn: chefData.user.nameEn?.trim(),
        username: chefData.user.username.trim(),
        email: chefData.user.email?.trim(),
        phone: chefData.user.phone?.trim(),
        isActive: chefData.user.isActive ?? true,
      },
      department: chefData.department,
    });
    console.log(`[${new Date().toISOString()}] chefsAPI.update - Response:`, response);
    return response;
  },
  delete: async (id: string) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] chefsAPI.delete - Invalid chef ID:`, id);
      throw new Error('Invalid chef ID');
    }
    const response = await api.delete(`/chefs/${id}`);
    console.log(`[${new Date().toISOString()}] chefsAPI.delete - Response:`, response);
    return response;
  },
  resetPassword: async (id: string, password: string) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] chefsAPI.resetPassword - Invalid chef ID:`, id);
      throw new Error('Invalid chef ID');
    }
    const response = await api.post(`/chefs/${id}/reset-password`, { password });
    console.log(`[${new Date().toISOString()}] chefsAPI.resetPassword response:`, response);
    return response;
  },
};




 export const departmentAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/departments', { params });
    console.log(`[${new Date().toISOString()}] departmentAPI.getAll - Response:`, response);
    return response;
  },
  getById: async (id) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] departmentAPI.getById - Invalid department ID:`, id);
      throw new Error('Invalid department ID');
    }
    const response = await api.get(`/departments/${id}`);
    console.log(`[${new Date().toISOString()}] departmentAPI.getById - Response:`, response);
    return response;
  },
  create: async (departmentData) => {
    const response = await api.post('/departments', {
      name: departmentData.name.trim(),
      nameEn: departmentData.nameEn?.trim(),
      code: departmentData.code.trim(),
      description: departmentData.description?.trim(),
    });
    console.log(`[${new Date().toISOString()}] departmentAPI.create - Response:`, response);
    return response;
  },
  update: async (id, departmentData) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] departmentAPI.update - Invalid department ID:`, id);
      throw new Error('Invalid department ID');
    }
    const response = await api.put(`/departments/${id}`, {
      name: departmentData.name?.trim(),
      nameEn: departmentData.nameEn?.trim(),
      code: departmentData.code?.trim(),
      description: departmentData.description?.trim(),
    });
    console.log(`[${new Date().toISOString()}] departmentAPI.update - Response:`, response);
    return response;
  },
  delete: async (id) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] departmentAPI.delete - Invalid department ID:`, id);
      throw new Error('Invalid department ID');
    }
    const response = await api.delete(`/departments/${id}`);
    console.log(`[${new Date().toISOString()}] departmentAPI.delete - Response:`, response);
    return response;
  },
};
export const ordersAPI = {
  create: async (orderData, isRtl = false) => {
    if (!isValidObjectId(orderData.branchId)) {
      throw new Error(createErrorMessage('invalidBranchId', isRtl));
    }
    try {
      const response = await api.post('/orders', {
        orderNumber: orderData.orderNumber.trim(),
        branchId: orderData.branchId,
        items: orderData.items.map((item) => ({
          product: item.product,
          quantity: item.quantity || 0,
          price: item.price,
        })),
        status: orderData.status.trim(),
      });
      return response;
    } catch (error) {
      throw new Error(isRtl ? `فشل في إنشاء الطلب: ${error.message}` : `Failed to create order: ${error.message}`);
    }
  },

  getAll: async (params = {}) => {
    try {
      const response = await api.get('/orders', { params });
      return response;
    } catch (error) {
      const isRtl = localStorage.getItem('language') === 'ar';
      throw new Error(isRtl ? `فشل في جلب الطلبات: ${error.message}` : `Failed to fetch orders: ${error.message}`);
    }
  },

  getById: async (orderId) => {
    if (!isValidObjectId(orderId)) {
      throw new Error(createErrorMessage('invalidOrderId', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.get(`/orders/${orderId}`);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch order: ${error.message}`);
    }
  },

  updateStatus: async (orderId, data) => {
    if (!isValidObjectId(orderId)) {
      throw new Error(createErrorMessage('invalidOrderId', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.patch(`/orders/${orderId}/status`, {
        status: data.status.trim(),
      });
      return response;
    } catch (error) {
      throw new Error(`Failed to update order status: ${error.message}`);
    }
  },

  updateChefItem: async (orderId, data) => {
    try {
      const response = await api.patch(`/orders/${orderId}/tasks/${data.taskId}/status`, {
        status: data.status.trim(),
        rejectReason: data.rejectReason || '',
      });
      return response;
    } catch (error) {
      throw new Error(`Failed to update item status: ${error.message}`);
    }
  },

  assignChef: async (orderId, data) => {
    try {
      const response = await api.patch(`/orders/${orderId}/assign`, {
        items: data.items,
        timestamp: new Date().toISOString(),
      });
      return response;
    } catch (error) {
      throw new Error(`Failed to assign chefs: ${error.message}`);
    }
  },

// ordersAPI.ts
confirmDelivery: async (orderId: string, receiptData: ItemReceipt[]) => {
  // تأكد إنك بترسل { items: [...] } وليس array مباشرة
  const payload = {
    items: receiptData.map(item => ({
      itemId: item.itemId,
      receivedQuantity: item.isRejected ? 0 : Number(item.receivedQuantity || 0),
      isRejected: item.isRejected,
      rejectReason: item.isRejected ? (item.rejectReason?.trim() || 'غير محدد') : undefined,
    })),
  };

  const response = await api.patch(`/orders/${orderId}/confirm-delivery`, payload);
  return response.data; // أو response حسب الـ wrapper
},
};

export const productionAssignmentsAPI = {
  create: async (assignmentData) => {
    if (
      !isValidObjectId(assignmentData.order) ||
      !isValidObjectId(assignmentData.product) ||
      !isValidObjectId(assignmentData.chef) ||
      !isValidObjectId(assignmentData.itemId)
    ) {
      console.error(`[${new Date().toISOString()}] productionAssignmentsAPI.create - Invalid data:`, assignmentData);
      throw new Error(createErrorMessage('invalidOrderOrItemOrChefId', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.post('/orders/tasks', assignmentData);
    console.log(`[${new Date().toISOString()}] productionAssignmentsAPI.create - Response:`, response);
    return response;
  },

  getChefTasks: async (chefId, query = {}) => {
    if (!isValidObjectId(chefId)) {
      console.error(`[${new Date().toISOString()}] productionAssignmentsAPI.getChefTasks - Invalid chefId:`, chefId);
      throw new Error('Invalid chef ID');
    }
    const response = await api.get(`/orders/tasks/chef/${chefId}`, { params: query });
    console.log(`[${new Date().toISOString()}] productionAssignmentsAPI.getChefTasks - Response:`, response);
    return response;
  },

  updateTaskStatus: async (orderId, taskId, data) => {
    if (!isValidObjectId(orderId) || !isValidObjectId(taskId)) {
      console.error(`[${new Date().toISOString()}] productionAssignmentsAPI.updateTaskStatus - Invalid orderId or taskId:`, { orderId, taskId });
      throw new Error(createErrorMessage('invalidOrderOrTaskId', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.patch(`/orders/${orderId}/tasks/${taskId}/status`, data);
    console.log(`[${new Date().toISOString()}] productionAssignmentsAPI.updateTaskStatus - Response:`, response);
    return response;
  },

 
 

  getAllTasks: async () => {
    const response = await api.get('/orders/tasks');
    console.log(`[${new Date().toISOString()}] productionAssignmentsAPI.getAllTasks - Response:`, response);
    return response;
  },


 updateMultipleItemsInOrder: async (orderId, data) => {
    if (!isValidObjectId(orderId)) {
      console.error(`[${new Date().toISOString()}] productionAssignmentsAPI.updateMultipleItemsInOrder - Invalid orderId:`, orderId);
      throw new Error(createErrorMessage('invalidOrderId', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.put(`/orders/${orderId}/items/multiple`, data); // ✅ تأكد من المسار
    console.log(`[${new Date().toISOString()}] productionAssignmentsAPI.updateMultipleItemsInOrder - Response:`, response);
    return response;
  },

  // ✅ دالة جديدة: تحديث كل العناصر في الطلب دفعة واحدة
  updateAllItemsInOrder: async (orderId, data) => {
    if (!isValidObjectId(orderId)) {
      console.error(`[${new Date().toISOString()}] productionAssignmentsAPI.updateAllItemsInOrder - Invalid orderId:`, orderId);
      throw new Error(createErrorMessage('invalidOrderId', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.put(`/orders/${orderId}/items/all`, data); // ✅ تأكد من المسار
    console.log(`[${new Date().toISOString()}] productionAssignmentsAPI.updateAllItemsInOrder - Response:`, response);
    return response;
  },
};


export const inventoryAPI = {
  getInventory: async (params = {}) => {
    if (params.branch && !isValidObjectId(params.branch)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getInventory - Invalid branch ID:`, params.branch);
      throw new Error(createErrorMessage('invalidBranchId', params.lang === 'ar'));
    }
    if (params.product && !isValidObjectId(params.product)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getInventory - Invalid product ID:`, params.product);
      throw new Error(createErrorMessage('invalidProductId', params.lang === 'ar'));
    }
    const response = await api.get('/inventory', { params });
    console.log(`[${new Date().toISOString()}] inventoryAPI.getInventory - Response:`, response);
    return response.inventory;
  },
  getByBranch: async (branchId) => {
    if (!isValidObjectId(branchId)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getByBranch - Invalid branch ID:`, branchId);
      throw new Error(createErrorMessage('invalidBranchId', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.get(`/inventory/branch/${branchId}`);
    console.log(`[${new Date().toISOString()}] inventoryAPI.getByBranch - Response:`, response);
    return response.inventory;
  },
  getAll: async (params = {}) => {
    if (params.branch && !isValidObjectId(params.branch)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getAll - Invalid branch ID:`, params.branch);
      throw new Error(createErrorMessage('invalidBranchId', params.lang === 'ar'));
    }
    if (params.product && !isValidObjectId(params.product)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getAll - Invalid product ID:`, params.product);
      throw new Error(createErrorMessage('invalidProductId', params.lang === 'ar'));
    }
    const response = await api.get('/inventory', { params });
    console.log(`[${new Date().toISOString()}] inventoryAPI.getAll - Response:`, response);
    return response.inventory;
  },
  create: async (data) => {
    if (
      !isValidObjectId(data.branchId) ||
      !isValidObjectId(data.productId) ||
      !isValidObjectId(data.userId) ||
      (data.orderId && !isValidObjectId(data.orderId))
    ) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.create - Invalid data:`, data);
      throw new Error(createErrorMessage('invalidBranchId', localStorage.getItem('language') === 'ar'));
    }
    if (data.currentStock < 0) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.create - Invalid stock quantity:`, data.currentStock);
      throw new Error(createErrorMessage('invalidStockQuantity', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && (data.minStockLevel < 0 || !Number.isInteger(data.minStockLevel))) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.create - Invalid minStockLevel:`, data.minStockLevel);
      throw new Error(createErrorMessage('invalidMinStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.maxStockLevel !== undefined && (data.maxStockLevel < 0 || !Number.isInteger(data.maxStockLevel))) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.create - Invalid maxStockLevel:`, data.maxStockLevel);
      throw new Error(createErrorMessage('invalidMaxStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && data.maxStockLevel !== undefined && data.maxStockLevel <= data.minStockLevel) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.create - Max stock level less than min:`, { minStockLevel: data.minStockLevel, maxStockLevel: data.maxStockLevel });
      throw new Error(createErrorMessage('maxLessThanMin', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.post('/inventory', {
      branchId: data.branchId,
      productId: data.productId,
      currentStock: data.currentStock,
      minStockLevel: data.minStockLevel ?? 0,
      maxStockLevel: data.maxStockLevel ?? 1000,
      userId: data.userId,
      orderId: data.orderId,
    });
    console.log(`[${new Date().toISOString()}] inventoryAPI.create - Response:`, response);
    return response.inventory;
  },
  bulkCreate: async (data) => {
    if (
      !isValidObjectId(data.branchId) ||
      !isValidObjectId(data.userId) ||
      (data.orderId && !isValidObjectId(data.orderId)) ||
      !Array.isArray(data.items) ||
      data.items.length === 0 ||
      data.items.some(item => !isValidObjectId(item.productId) || item.currentStock < 0)
    ) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.bulkCreate - Invalid data:`, data);
      throw new Error(createErrorMessage('invalidBranchId', localStorage.getItem('language') === 'ar'));
    }
    if (data.items.some(item => item.minStockLevel !== undefined && (item.minStockLevel < 0 || !Number.isInteger(item.minStockLevel)))) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.bulkCreate - Invalid minStockLevel:`, data.items);
      throw new Error(createErrorMessage('invalidMinStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.items.some(item => item.maxStockLevel !== undefined && (item.maxStockLevel < 0 || !Number.isInteger(item.maxStockLevel)))) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.bulkCreate - Invalid maxStockLevel:`, data.items);
      throw new Error(createErrorMessage('invalidMaxStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.items.some(item => item.minStockLevel !== undefined && item.maxStockLevel !== undefined && item.maxStockLevel <= item.minStockLevel)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.bulkCreate - Max stock level less than min:`, data.items);
      throw new Error(createErrorMessage('maxLessThanMin', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.post('/inventory/bulk', {
      branchId: data.branchId,
      userId: data.userId,
      orderId: data.orderId,
      items: data.items.map(item => ({
        productId: item.productId,
        currentStock: item.currentStock,
        minStockLevel: item.minStockLevel ?? 0,
        maxStockLevel: item.maxStockLevel ?? 1000,
      })),
    });
    console.log(`[${new Date().toISOString()}] inventoryAPI.bulkCreate - Response:`, response);
    return response.inventories;
  },
  updateStock: async (id, data) => {
    if (
      !isValidObjectId(id) ||
      (data.productId && !isValidObjectId(data.productId)) ||
      (data.branchId && !isValidObjectId(data.branchId))
    ) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.updateStock - Invalid inventory ID, product ID, or branch ID:`, { id, data });
      throw new Error(createErrorMessage('invalidBranchId', localStorage.getItem('language') === 'ar'));
    }
    if (data.currentStock !== undefined && (data.currentStock < 0 || !Number.isInteger(data.currentStock))) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.updateStock - Invalid stock quantity:`, data.currentStock);
      throw new Error(createErrorMessage('invalidStockQuantity', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && (data.minStockLevel < 0 || !Number.isInteger(data.minStockLevel))) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.updateStock - Invalid minStockLevel:`, data.minStockLevel);
      throw new Error(createErrorMessage('invalidMinStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.maxStockLevel !== undefined && (data.maxStockLevel < 0 || !Number.isInteger(data.maxStockLevel))) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.updateStock - Invalid maxStockLevel:`, data.maxStockLevel);
      throw new Error(createErrorMessage('invalidMaxStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && data.maxStockLevel !== undefined && data.maxStockLevel <= data.minStockLevel) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.updateStock - Max stock level less than min:`, { minStockLevel: data.minStockLevel, maxStockLevel: data.maxStockLevel });
      throw new Error(createErrorMessage('maxLessThanMin', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.put(`/inventory/${id}`, {
      currentStock: data.currentStock,
      minStockLevel: data.minStockLevel,
      maxStockLevel: data.maxStockLevel,
      productId: data.productId,
      branchId: data.branchId,
    });
    console.log(`[${new Date().toISOString()}] inventoryAPI.updateStock - Response:`, response);
    return response.inventory;
  },
  processReturnItems: async (returnId, data) => {
    const language = localStorage.getItem('language') || 'en';
    const isRtl = language === 'ar';

    if (
      !isValidObjectId(returnId) ||
      !isValidObjectId(data.branchId) ||
      !Array.isArray(data.items) ||
      data.items.length === 0 ||
      data.items.some(item => !isValidObjectId(item.productId) || item.quantity < 1 || !['approved', 'rejected'].includes(item.status))
    ) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.processReturnItems - Invalid data:`, { returnId, data });
      throw new Error(createErrorMessage('invalidBranchId', isRtl));
    }

    try {
      const response = await api.patch(`/inventory/returns/${returnId}/process`, {
        branchId: data.branchId,
        items: data.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          status: item.status,
          reviewNotes: item.reviewNotes?.trim(),
        })),
      });
      console.log(`[${new Date().toISOString()}] inventoryAPI.processReturnItems - Response:`, response);
      return response.returnRequest;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.processReturnItems - Error:`, {
        message: error.message,
        status: error.status,
        details: error.details,
        response: error.response,
      });
      let errorMessage = error.message || (isRtl ? 'خطأ في معالجة عناصر الإرجاع' : 'Error processing return items');
      if (error.status === 500) {
        errorMessage = isRtl ? 'خطأ في الخادم، حاول مرة أخرى لاحقًا' : 'Server error, please try again later';
      }
      throw new Error(errorMessage);
    }
  },
  createReturn: async (data) => {
    const language = localStorage.getItem('language') || 'en';
    const isRtl = language === 'ar';

    if (
      !isValidObjectId(data.orderId) ||
      !isValidObjectId(data.branchId) ||
      !data.reason ||
      !Array.isArray(data.items) ||
      data.items.length === 0 ||
      data.items.some(item => 
        (item.itemId && !isValidObjectId(item.itemId)) || 
        !isValidObjectId(item.productId) || 
        item.quantity < 1 || 
        !item.reason
      )
    ) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.createReturn - Invalid data:`, data);
      throw new Error(createErrorMessage('invalidBranchId', isRtl));
    }

    try {
      const response = await api.post('/inventory/returns', {
        orderId: data.orderId,
        branchId: data.branchId,
        reason: data.reason.trim(),
        items: data.items.map(item => ({
          itemId: item.itemId,
          productId: item.productId,
          quantity: item.quantity,
          reason: item.reason.trim(),
          reasonEn: item.reasonEn?.trim(),
        })),
        notes: data.notes?.trim(),
      });
      console.log(`[${new Date().toISOString()}] inventoryAPI.createReturn - Response:`, response);
      return response.returnRequest;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.createReturn - Error:`, {
        message: error.message,
        status: error.status,
        details: error.details,
        response: error.response,
      });
      let errorMessage = error.message || (isRtl ? 'خطأ في إنشاء طلب الإرجاع' : 'Error creating return request');
      if (error.status === 500) {
        errorMessage = isRtl ? 'خطأ في الخادم، حاول مرة أخرى لاحقًا' : 'Server error, please try again later';
      }
      throw new Error(errorMessage);
    }
  },
  createRestockRequest: async (data) => {
    if (
      !isValidObjectId(data.productId) ||
      !isValidObjectId(data.branchId) ||
      data.requestedQuantity < 1
    ) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.createRestockRequest - Invalid data:`, data);
      throw new Error(createErrorMessage('invalidBranchId', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.post('/inventory/restock-requests', {
      productId: data.productId,
      branchId: data.branchId,
      requestedQuantity: data.requestedQuantity,
      notes: data.notes?.trim(),
    });
    console.log(`[${new Date().toISOString()}] inventoryAPI.createRestockRequest - Response:`, response);
    return response.restockRequest;
  },
  getRestockRequests: async (params = {}) => {
    if (params.branchId && !isValidObjectId(params.branchId)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getRestockRequests - Invalid branch ID:`, params.branchId);
      throw new Error(createErrorMessage('invalidBranchId', params.lang === 'ar'));
    }
    const response = await api.get('/inventory/restock-requests', { params });
    console.log(`[${new Date().toISOString()}] inventoryAPI.getRestockRequests - Response:`, response);
    return response.restockRequests;
  },
  approveRestockRequest: async (requestId, data) => {
    if (!isValidObjectId(requestId) || !isValidObjectId(data.userId) || data.approvedQuantity < 1) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.approveRestockRequest - Invalid data:`, { requestId, data });
      throw new Error(createErrorMessage('invalidBranchId', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.patch(`/inventory/restock-requests/${requestId}/approve`, {
      approvedQuantity: data.approvedQuantity,
      userId: data.userId,
    });
    console.log(`[${new Date().toISOString()}] inventoryAPI.approveRestockRequest - Response:`, response);
    return response.restockRequest;
  },
  getHistory: async (params = {}) => {
    if (params.branchId && !isValidObjectId(params.branchId)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getHistory - Invalid branch ID:`, params.branchId);
      throw new Error(createErrorMessage('invalidBranchId', params.lang === 'ar'));
    }
    if (params.productId && !isValidObjectId(params.productId)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.getHistory - Invalid product ID:`, params.productId);
      throw new Error(createErrorMessage('invalidProductId', params.lang === 'ar'));
    }
    const response = await api.get('/inventory/history', { params });
    console.log(`[${new Date().toISOString()}] inventoryAPI.getHistory - Response:`, response);
    return response.history;
  },
  checkOrderHistory: async (orderId) => {
    if (!isValidObjectId(orderId)) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.checkOrderHistory - Invalid order ID:`, orderId);
      throw new Error(createErrorMessage('invalidOrderId', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.get(`/inventory/check-order-history/${orderId}`);
      console.log(`[${new Date().toISOString()}] inventoryAPI.checkOrderHistory - Response:`, response);
      return response.exists;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] inventoryAPI.checkOrderHistory - Error:`, {
        message: error.message,
        status: error.status,
        details: error.details,
        response: error.response,
      });
      let errorMessage = error.message || (localStorage.getItem('language') === 'ar' ? 'خطأ في التحقق من سجل الطلب' : 'Error checking order history');
      if (error.status === 500) {
        errorMessage = localStorage.getItem('language') === 'ar' ? 'خطأ في الخادم، حاول مرة أخرى لاحقًا' : 'Server error, please try again later';
      }
      throw new Error(errorMessage);
    }
  },
};




export const reportsAPI = {
  // ✅ التقرير اليومي مع معالجة أخطاء محسنة
   getDailySummary: async (params = {}) => {
    try {
      const cleanParams = { ...params };
      
      // ✅ تنظيف المعلمات
      if (params.startDate) {
        try {
          const date = new Date(params.startDate);
          cleanParams.startDate = date.toISOString().split('T')[0];
        } catch {
          const today = new Date();
          cleanParams.startDate = today.toISOString().split('T')[0];
        }
      }
      
      if (params.endDate) {
        try {
          const date = new Date(params.endDate);
          cleanParams.endDate = date.toISOString().split('T')[0];
        } catch {
          const today = new Date();
          cleanParams.endDate = today.toISOString().split('T')[0];
        }
      }
      
      // ✅ القيم الافتراضية
      if (!cleanParams.startDate) {
        const today = new Date();
        cleanParams.startDate = today.toISOString().split('T')[0];
      }
      
      if (!cleanParams.endDate) {
        const today = new Date();
        cleanParams.endDate = today.toISOString().split('T')[0];
      }
      
      // ✅ التحقق من language
      if (!cleanParams.isRtl) {
        const language = localStorage.getItem('language') || 'ar';
        cleanParams.isRtl = language === 'ar';
      }
      
      console.log(`[${new Date().toISOString()}] Daily summary request:`, cleanParams);
      
      const response = await api.get('/reports/daily-summary', { 
        params: cleanParams,
        timeout: 15000
      });
      
      return response;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Daily summary error:`, error);
      
      // ✅ Return fallback data for server errors
      if (error.status === 500) {
        const language = localStorage.getItem('language') || 'ar';
        const isRtl = language === 'ar';
        
        return {
          success: true,
          data: [],
          totals: { grandTotalQuantity: 0, grandTotalAmount: 0 },
          period: 'N/A',
          groupBy: params.groupBy || 'product',
          branches: [],
          generatedAt: new Date().toISOString(),
          cached: false,
          message: isRtl ? 'جارٍ تحسين النظام...' : 'System optimization in progress...'
        };
      }
      
      throw error;
    }
  },

  // ✅ الإحصائيات السريعة
  getQuickStats: async (params = {}) => {
    try {
      const cleanParams = { ...params };
      
      // ✅ إضافة language إذا لم يكن موجوداً
      if (!cleanParams.isRtl) {
        const language = localStorage.getItem('language') || 'ar';
        cleanParams.isRtl = language === 'ar';
      }
      
      console.log(`[${new Date().toISOString()}] Quick stats request:`, cleanParams);
      
      const response = await api.get('/reports/quick-stats', { 
        params: cleanParams,
        timeout: 10000
      });
      
      return response;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Quick stats error:`, error);
      
      // ✅ Return fallback data
      if (error.status === 500) {
        return {
          success: true,
          data: {
            totalOrders: 0,
            pendingOrders: 0,
            inProductionOrders: 0,
            deliveredOrders: 0,
            totalRevenue: 0,
            avgOrderValue: 0,
            todayOrders: 0,
            todayRevenue: 0,
            period: 'N/A',
            generatedAt: new Date().toISOString()
          },
          cached: false
        };
      }
      
      throw error;
    }
  },

  // ✅ تقرير المبيعات
  getSalesReport: async (params = {}) => {
    const response = await api.get('/reports/sales-report', { params });
    return response;
  },

  // ✅ الطلبات الحية
  getLiveOrders: async (params = {}) => {
    const response = await api.get('/reports/live-orders', { params });
    return response;
  },

  // ✅ تقرير مقارنة الطلبات والمرتجعات (المحدث بالكامل)
  getOrdersVsReturns: async (params = {}) => {
    try {
      const language = localStorage.getItem('language') || 'ar';
      const isRtl = language === 'ar';
      
      const cleanParams = { 
        ...params,
        lang: language // ✅ إضافة اللغة للطلب
      };
      
      // ✅ معالجة التواريخ بشكل آمن
      const processDate = (dateStr) => {
        if (!dateStr) return null;
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return null;
          return date.toISOString().split('T')[0];
        } catch {
          return null;
        }
      };
      
      cleanParams.startDate = processDate(params.startDate);
      cleanParams.endDate = processDate(params.endDate);
      
      // ✅ القيم الافتراضية للتواريخ
      if (!cleanParams.startDate) {
        const today = new Date();
        cleanParams.startDate = today.toISOString().split('T')[0];
      }
      
      if (!cleanParams.endDate) {
        const today = new Date();
        cleanParams.endDate = today.toISOString().split('T')[0];
      }
      
      // ✅ معالجة التجميع
      if (!cleanParams.groupBy || !['product', 'department'].includes(cleanParams.groupBy)) {
        cleanParams.groupBy = 'product';
      }
      
      // ✅ معالجة الأقسام
      if (cleanParams.department) {
        if (Array.isArray(cleanParams.department)) {
          cleanParams.department = cleanParams.department
            .filter(d => d && d !== 'all')
            .join(',');
        } else if (cleanParams.department === 'all') {
          delete cleanParams.department;
        }
      }
      
      // ✅ إضافة isRtl للـ API
      cleanParams.isRtl = isRtl.toString();
      
      console.log('📤 Orders vs Returns request:', cleanParams);
      
      const response = await api.get('/reports/orders-vs-returns', { 
        params: cleanParams,
        timeout: 30000
      });
      
      // ✅ التحقق من استجابة الـ API
      if (!response || !response.success) {
        throw new Error(response?.message || 'API returned unsuccessful response');
      }
      
      return response;
    } catch (error) {
      console.error('❌ Orders vs Returns error:', error);
      
      const language = localStorage.getItem('language') || 'ar';
      const isRtl = language === 'ar';
      
      return {
        success: true,
        message: isRtl ? 'جارٍ تحسين النظام...' : 'System optimization in progress...',
        data: [],
        totals: {
          totalOrders: 0,
          totalReturns: 0,
          totalNet: 0,
          avgReturnRate: 0,
          branchTotals: {}
        },
        branches: [],
        departments: [],
        period: 'N/A',
        generatedAt: new Date().toISOString(),
        cached: false
      };
    }
  },

  // ✅ تصدير التقرير
  exportReport: async (data) => {
    const response = await api.post('/reports/export', data);
    return response;
  }
};


export const factoryInventoryAPI = {
  getAll: async (params = {}) => {
    if (params.product && !isValidObjectId(params.product)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.getAll - Invalid product ID:`, params.product);
      throw new Error(createErrorMessage('invalidProductId', params.lang === 'ar'));
    }
    if (params.department && !isValidObjectId(params.department)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.getAll - Invalid department ID:`, params.department);
      throw new Error(createErrorMessage('invalidDepartmentId', params.lang === 'ar'));
    }
    const response = await api.get('/factoryInventory', { params: params });
    console.log(`[${new Date().toISOString()}] factoryInventoryAPI.getAll - Response:`, response);
    return response;
  },
  create: async (data) => {
    if (!isValidObjectId(data.productId) || !isValidObjectId(data.userId) || (data.orderId && !isValidObjectId(data.orderId))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.create - Invalid data:`, data);
      throw new Error(createErrorMessage('invalidProductId', localStorage.getItem('language') === 'ar'));
    }
    if (data.currentStock < 0) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.create - Invalid stock quantity:`, data.currentStock);
      throw new Error(createErrorMessage('invalidStockQuantity', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && (data.minStockLevel < 0 || !Number.isInteger(data.minStockLevel))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.create - Invalid minStockLevel:`, data.minStockLevel);
      throw new Error(createErrorMessage('invalidMinStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.maxStockLevel !== undefined && (data.maxStockLevel < 0 || !Number.isInteger(data.maxStockLevel))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.create - Invalid maxStockLevel:`, data.maxStockLevel);
      throw new Error(createErrorMessage('invalidMaxStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && data.maxStockLevel !== undefined && data.maxStockLevel <= data.minStockLevel) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.create - Max stock level less than min:`, { minStockLevel: data.minStockLevel, maxStockLevel: data.maxStockLevel });
      throw new Error(createErrorMessage('maxLessThanMin', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.post('/factoryInventory', {
      productId: data.productId,
      userId: data.userId,
      currentStock: data.currentStock,
      minStockLevel: data.minStockLevel ?? 0,
      maxStockLevel: data.maxStockLevel ?? 1000,
      orderId: data.orderId,
    });
    console.log(`[${new Date().toISOString()}] factoryInventoryAPI.create - Response:`, response);
    return response;
  },
  bulkCreate: async (data) => {
    if (!isValidObjectId(data.userId) || (data.orderId && !isValidObjectId(data.orderId)) || !Array.isArray(data.items) || data.items.length === 0 || data.items.some(item => !isValidObjectId(item.productId))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.bulkCreate - Invalid data:`, data);
      throw new Error(createErrorMessage('invalidUserId', localStorage.getItem('language') === 'ar'));
    }
    if (data.items.some(item => item.currentStock < 0)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.bulkCreate - Invalid stock quantity:`, data.items);
      throw new Error(createErrorMessage('invalidStockQuantity', localStorage.getItem('language') === 'ar'));
    }
    if (data.items.some(item => item.minStockLevel !== undefined && (item.minStockLevel < 0 || !Number.isInteger(item.minStockLevel)))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.bulkCreate - Invalid minStockLevel:`, data.items);
      throw new Error(createErrorMessage('invalidMinStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.items.some(item => item.maxStockLevel !== undefined && (item.maxStockLevel < 0 || !Number.isInteger(item.maxStockLevel)))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.bulkCreate - Invalid maxStockLevel:`, data.items);
      throw new Error(createErrorMessage('invalidMaxStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.items.some(item => item.minStockLevel !== undefined && item.maxStockLevel !== undefined && item.maxStockLevel <= item.minStockLevel)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.bulkCreate - Max stock level less than min:`, data.items);
      throw new Error(createErrorMessage('maxLessThanMin', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.post('/factoryInventory/bulk', {
      userId: data.userId,
      orderId: data.orderId,
      items: data.items.map(item => ({
        productId: item.productId,
        currentStock: item.currentStock,
        minStockLevel: item.minStockLevel ?? 0,
        maxStockLevel: item.maxStockLevel ?? 1000,
      })),
    });
    console.log(`[${new Date().toISOString()}] factoryInventoryAPI.bulkCreate - Response:`, response);
    return response;
  },
  updateStock: async (id, data) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.updateStock - Invalid inventory ID:`, id);
      throw new Error(createErrorMessage('invalidInventoryId', localStorage.getItem('language') === 'ar'));
    }
    if (data.currentStock !== undefined && (data.currentStock < 0 || !Number.isInteger(data.currentStock))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.updateStock - Invalid stock quantity:`, data.currentStock);
      throw new Error(createErrorMessage('invalidStockQuantity', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && (data.minStockLevel < 0 || !Number.isInteger(data.minStockLevel))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.updateStock - Invalid minStockLevel:`, data.minStockLevel);
      throw new Error(createErrorMessage('invalidMinStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.maxStockLevel !== undefined && (data.maxStockLevel < 0 || !Number.isInteger(data.maxStockLevel))) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.updateStock - Invalid maxStockLevel:`, data.maxStockLevel);
      throw new Error(createErrorMessage('invalidMaxStockLevel', localStorage.getItem('language') === 'ar'));
    }
    if (data.minStockLevel !== undefined && data.maxStockLevel !== undefined && data.maxStockLevel <= data.minStockLevel) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.updateStock - Max stock level less than min:`, { minStockLevel: data.minStockLevel, maxStockLevel: data.maxStockLevel });
      throw new Error(createErrorMessage('maxLessThanMin', localStorage.getItem('language') === 'ar'));
    }
    const response = await api.put(`/factoryInventory/${id}`, {
      currentStock: data.currentStock,
      minStockLevel: data.minStockLevel,
      maxStockLevel: data.maxStockLevel,
    });
    console.log(`[${new Date().toISOString()}] factoryInventoryAPI.updateStock - Response:`, response);
    return response;
  },
  getHistory: async (params = {}) => {
    if (params.productId && !isValidObjectId(params.productId)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.getHistory - Invalid product ID:`, params.productId);
      throw new Error(createErrorMessage('invalidProductId', params.lang === 'ar'));
    }
    if (params.department && !isValidObjectId(params.department)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.getHistory - Invalid department ID:`, params.department);
      throw new Error(createErrorMessage('invalidDepartmentId', params.lang === 'ar'));
    }
    if (params.period && !['daily', 'weekly', 'monthly'].includes(params.period)) {
      console.error(`[${new Date().toISOString()}] factoryInventoryAPI.getHistory - Invalid period:`, params.period);
      throw new Error(createErrorMessage('invalidStatus', params.lang === 'ar'));
    }
    const response = await api.get('/factoryInventory/history', { params: params });
    console.log(`[${new Date().toISOString()}] factoryInventoryAPI.getHistory - Response:`, response);
    return response;
  },
  getAvailableProducts: async (params = {}) => {
    const response = await api.get('/products', { params: params });
    console.log(`[${new Date().toISOString()}] factoryInventoryAPI.getAvailableProducts - Response:`, response);
    return response;
  },
};

export const factoryOrdersAPI = {
  // إنشاء طلب إنتاج جديد
  create: async (data: {
    orderNumber: string;
    items: { product: string; quantity: number; assignedTo?: string }[];
    notes?: string;
    priority?: string;
  }) => {
    // التحقق من صحة البيانات المدخلة
    if (
      !data.orderNumber ||
      !data.orderNumber.trim() ||
      !Array.isArray(data.items) ||
      data.items.length === 0 ||
      data.items.some(
        (item) => !isValidObjectId(item.product) || !Number.isInteger(item.quantity) || item.quantity < 1
      )
    ) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.create - البيانات غير صالحة:`, data);
      throw new Error(createErrorMessage('invalidItems', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.post('/factoryOrders', {
        orderNumber: data.orderNumber.trim(),
        items: data.items.map((item) => ({
          product: item.product,
          quantity: item.quantity,
          assignedTo: item.assignedTo,
        })),
        notes: data.notes?.trim() || '',
        priority: data.priority?.trim() || 'medium',
      });
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.create - الاستجابة:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.create - خطأ:`, error.message);
      throw new Error(createErrorMessage('invalidData', localStorage.getItem('language') === 'ar'));
    }
  },
  // استرجاع جميع طلبات الإنتاج مع معايير اختيارية
  getAll: async (query: Record<string, any> = {}) => {
    try {
      const response = await api.get('/factoryOrders', { params: query });
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.getAll - الاستجابة:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.getAll - خطأ:`, error.message);
      throw error;
    }
  },
  // استرجاع طلب إنتاج محدد باستخدام المعرف
  getById: async (id: string) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.getById - معرف طلب غير صالح:`, id);
      throw new Error(createErrorMessage('invalidFactoryOrderId', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.get(`/factoryOrders/${id}`);
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.getById - الاستجابة:`, response);
      return response.data;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.getById - خطأ:`, error.message);
      throw error;
    }
  },
  // الموافقة على طلب إنتاج
  approve: async (id: string) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.approve - معرف طلب غير صالح:`, id);
      throw new Error(createErrorMessage('invalidFactoryOrderId', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.patch(`/factoryOrders/${id}/approve`);
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.approve - الاستجابة:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.approve - خطأ:`, error.message);
      throw error;
    }
  },
  // تعيين شيفات لعناصر طلب الإنتاج
  assignChefs: async (id: string, data: { items: { itemId: string; assignedTo: string }[] }) => {
    if (
      !isValidObjectId(id) ||
      !Array.isArray(data.items) ||
      data.items.length === 0 ||
      data.items.some((item) => !isValidObjectId(item.itemId) || !isValidObjectId(item.assignedTo))
    ) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.assignChefs - بيانات غير صالحة:`, { id, data });
      throw new Error(createErrorMessage('invalidData', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.patch(`/factoryOrders/${id}/assign`, {
        items: data.items.map((item) => ({
          itemId: item.itemId,
          assignedTo: item.assignedTo,
        })),
      });
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.assignChefs - الاستجابة:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.assignChefs - خطأ:`, error.message);
      throw error;
    }
  },
  // تحديث حالة عنصر في طلب الإنتاج
  updateItemStatus: async (id: string, itemId: string, data: { status: string }) => {
    if (!isValidObjectId(id) || !isValidObjectId(itemId)) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.updateItemStatus - معرفات غير صالحة:`, { id, itemId });
      throw new Error(createErrorMessage('invalidId', localStorage.getItem('language') === 'ar'));
    }
    if (!['pending', 'assigned', 'in_progress', 'completed'].includes(data.status)) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.updateItemStatus - حالة غير صالحة:`, data.status);
      throw new Error(createErrorMessage('invalidStatus', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.patch(`/factoryOrders/${id}/items/${itemId}/status`, data);
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.updateItemStatus - الاستجابة:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.updateItemStatus - خطأ:`, error.message);
      throw error;
    }
  },
  // تحديث حالة طلب الإنتاج
  updateStatus: async (id: string, data: { status: string }) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.updateStatus - معرف طلب غير صالح:`, id);
      throw new Error(createErrorMessage('invalidFactoryOrderId', localStorage.getItem('language') === 'ar'));
    }
    if (!['requested', 'pending', 'approved', 'in_production', 'completed', 'cancelled'].includes(data.status)) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.updateStatus - حالة غير صالحة:`, data.status);
      throw new Error(createErrorMessage('invalidStatus', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.patch(`/factoryOrders/${id}/status`, data);
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.updateStatus - الاستجابة:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.updateStatus - خطأ:`, error.message);
      throw error;
    }
  },
  // تأكيد الإنتاج لطلب الإنتاج
  confirmProduction: async (id: string) => {
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.confirmProduction - معرف طلب غير صالح:`, id);
      throw new Error(createErrorMessage('invalidFactoryOrderId', localStorage.getItem('language') === 'ar'));
    }
    try {
      const response = await api.patch(`/factoryOrders/${id}/confirm-production`);
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.confirmProduction - الاستجابة:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.confirmProduction - خطأ:`, error.message);
      throw error;
    }
  },
  // استرجاع المنتجات المتاحة للمستخدم الحالي
  getAvailableProducts: async () => {
    try {
      const response = await api.get('/factoryOrders/available-products');
      console.log(`[${new Date().toISOString()}] factoryOrdersAPI.getAvailableProducts - الاستجابة:`, response);
      return response.data.data;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] factoryOrdersAPI.getAvailableProducts - خطأ:`, error.message);
      throw error;
    }
  },
};



export { notificationsAPI, returnsAPI, salesAPI  };


export interface SocketReturnEvent {
  returnId: string;
  branchId: string;
  returnNumber: string;
  status?: string;
  eventId: string;
}
export default api;